import React from "react";
import { useApp } from "../AppContext";

/**
 * Country-aware flag.
 * - DR (Dominican Republic): authentic 4-quadrant + white cross + crest (SVG)
 * - Any other country preset: generic 3-stripe driven by --flag-1/2/3 CSS variables
 *
 * Props:
 *   className – sizing/positioning classes
 *   style     – inline style overrides
 *   horizontal – when true, renders stripes horizontally (only for non-DR stripes)
 *   variant   – "auto" (default, picks based on country) | "stripes" | "cross"
 */
const DominicanFlag = ({ className = "", style, horizontal = false, variant = "auto" }) => {
    const { branding } = useApp() || {};
    const isDR = branding?.country_code === "DR";
    const useCross = variant === "cross" || (variant === "auto" && isDR);

    if (useCross) {
        // Authentic Dominican Republic flag: blue (top-left, bottom-right),
        // red (top-right, bottom-left), white cross, simplified crest disc.
        return (
            <div
                className={`overflow-hidden ${className}`}
                style={style}
                role="img"
                aria-label="Dominican Republic flag"
            >
                <svg
                    viewBox="0 0 60 40"
                    preserveAspectRatio="none"
                    className="block w-full h-full"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    {/* quadrants */}
                    <rect x="0" y="0" width="30" height="20" fill="#002D62" />
                    <rect x="30" y="0" width="30" height="20" fill="#CE1126" />
                    <rect x="0" y="20" width="30" height="20" fill="#CE1126" />
                    <rect x="30" y="20" width="30" height="20" fill="#002D62" />
                    {/* white cross */}
                    <rect x="0" y="17" width="60" height="6" fill="#FFFFFF" />
                    <rect x="27" y="0" width="6" height="40" fill="#FFFFFF" />
                    {/* simplified crest at center */}
                    <circle cx="30" cy="20" r="3.2" fill="#FFFFFF" stroke="#0F7B3D" strokeWidth="0.5" />
                    <rect x="28.5" y="18.5" width="3" height="3" fill="#CE1126" />
                </svg>
            </div>
        );
    }

    // Generic 3-stripe (for non-DR country presets)
    const stripeStyle = (cssVar) => ({ background: `var(${cssVar}, #888)` });
    return (
        <div
            className={`flex overflow-hidden ${horizontal ? "flex-col" : ""} ${className}`}
            style={style}
            role="img"
            aria-label="Country flag"
        >
            <div className="flex-1" style={stripeStyle("--flag-1")} />
            <div className="flex-1" style={stripeStyle("--flag-2")} />
            <div className="flex-1" style={stripeStyle("--flag-3")} />
        </div>
    );
};

export default DominicanFlag;
